import { DownOutlined } from "@ant-design/icons";
import {
  AGENTIC_ACTIVITY_FILTER_KEYS,
  AgentActivityProtocols,
  AgentActivityRiskLevels,
  SELECT_FILTER_PARAMS,
  TEXT_FILTER_PARAMS,
} from "@p0security/integrations-agentic-shared/interface";
import type {
  AgentActivityEntry,
  AgentActivityProtocol,
  AgentActivityRiskLevelParam,
  AgenticActivityFilters,
  TextFilter,
} from "@p0security/integrations-agentic-shared/interface";
import type { FormItemProps } from "@p0security/ui";
import {
  AutoComplete,
  Button,
  Col,
  Flex,
  Form,
  Row,
  Select,
  Spacing,
  Tag,
} from "@p0security/ui";
import { CustomDatePicker } from "components/DatePicker";
import { isValid, parseISO } from "date-fns";
import type { RangePickerProps } from "rc-picker";
import { useCallback } from "react";

import { RiskTag } from "./components/RiskTag.tsx";
import { eventProtocolViews } from "./eventProtocols.tsx";
import type { TimeRange } from "./timeRange.ts";
import type { Lookback } from "./timeRange.ts";
import {
  DEFAULT_LOOKBACK,
  DEFAULT_TIME_RANGE,
  LOOKBACK_LABELS,
} from "./timeRange.ts";

export type DateRange = RangePickerProps<Date>["value"];

type FilterSuggestions = Record<TextFilter, string[]>;

/** A lookback picks a rolling window; "custom" swaps in the `timeRange`
 * picker for absolute bounds. */
type RangeChoice = Lookback | "custom";

type FilterFormValues = Partial<Record<TextFilter, string>> & {
  protocol?: AgentActivityProtocol;
  rangeChoice?: RangeChoice;
  risk?: AgentActivityRiskLevelParam;
  timeRange?: DateRange;
};

const RANGE_CHOICE_OPTIONS: {
  label: React.ReactNode;
  title: string;
  value: RangeChoice;
}[] = [
  ...LOOKBACK_LABELS.map(({ label, value }) => ({
    label:
      value === DEFAULT_LOOKBACK ? (
        <Flex align="center" gap={8}>
          {label}
          <Tag>Default</Tag>
        </Flex>
      ) : (
        label
      ),
    title: label,
    value: value as RangeChoice,
  })),
  { label: "Custom range", title: "Custom range", value: "custom" },
];

export const parseTimeParam = (value: string | null): Date | null => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

const toFormValues = (
  filters: AgenticActivityFilters,
  timeRange: TimeRange = DEFAULT_TIME_RANGE
): FilterFormValues => ({
  ...filters,
  rangeChoice: timeRange.type === "custom" ? "custom" : timeRange.lookback,
  timeRange:
    timeRange.type === "custom"
      ? [timeRange.startTime, timeRange.endTime]
      : undefined,
});

const toTimeRange = (
  rangeChoice: RangeChoice | undefined,
  range: DateRange
): TimeRange => {
  if (rangeChoice !== "custom")
    return { lookback: rangeChoice ?? DEFAULT_LOOKBACK, type: "relative" };
  const [startTime, endTime] = range ?? [];
  return startTime && endTime
    ? { endTime, startTime, type: "custom" }
    : DEFAULT_TIME_RANGE;
};

// setFieldsValue only touches the keys it is given, so name every filter.
// `timeRange: undefined` keeps antd's recursive Partial from deep-partialing
// the Dates in a range, which it cannot assign back.
const EMPTY_VALUES = {
  ...toFormValues(
    Object.fromEntries(AGENTIC_ACTIVITY_FILTER_KEYS.map((k) => [k, undefined]))
  ),
  timeRange: undefined,
};

const CUSTOM_RANGE_RULES: FormItemProps["rules"] = [
  {
    async validator(_, value: DateRange) {
      if (!value?.[0] || !value?.[1])
        throw new Error("Select a start and end time.");
    },
  },
];

const PROTOCOL_OPTIONS = AgentActivityProtocols.map((value) => ({
  label: eventProtocolViews[value].label,
  value,
}));

const RISK_OPTIONS = AgentActivityRiskLevels.map((level) => ({
  label: (
    <RiskTag
      hideTooltip
      resolvedRisk={{ level, explanation: { source: "annotation" } }}
      size="small"
    />
  ),
  // The option's value is the filter's wire form (lowercase, matching
  // Protocol's "mcp"/"a2a") -- distinct from RiskTag's own `level` prop
  // above, which stays the capitalized display form.
  value: level.toLowerCase() as AgentActivityRiskLevelParam,
}));

// At xl the grid is 24 columns wide. These spans, plus Protocol and Risk's
// own 3 each, exactly fill two rows: classification and identity on the
// first, the call's target on the second.
const FILTER_DEFINITIONS: {
  ariaLabel: string;
  key: TextFilter;
  label: string;
  placeholder: string;
  xlSpan: number;
}[] = [
  {
    ariaLabel: "Filter by user",
    key: "caller",
    label: "User",
    placeholder: "Email or JWT subject",
    xlSpan: 6,
  },
  {
    ariaLabel: "Filter by client ID",
    key: "clientId",
    label: "Client ID",
    placeholder: "Agentic client ID",
    xlSpan: 6,
  },
  {
    ariaLabel: "Filter by tool name",
    key: "toolName",
    label: "Tool Name",
    placeholder: "MCP tool name",
    xlSpan: 6,
  },
  {
    ariaLabel: "Filter by agent name",
    key: "agentId",
    label: "Agent Name",
    placeholder: "A2A agent name",
    xlSpan: 7,
  },
  {
    ariaLabel: "Filter by server or bridge",
    key: "mcpServerName",
    label: "Server / Bridge",
    placeholder: "Server or bridge name",
    xlSpan: 9,
  },
  {
    ariaLabel: "Filter by session ID",
    key: "sessionId",
    label: "Session ID",
    placeholder: "Session ID",
    xlSpan: 8,
  },
];

/** Returns a fresh tenant-local set of event filter suggestions. */
export const newFilterSuggestions = (): FilterSuggestions => ({
  agentId: [],
  caller: [],
  clientId: [],
  mcpServerName: [],
  sessionId: [],
  toolName: [],
});

/** Adds filter values discovered in loaded events to existing suggestions. */
export const mergeFilterSuggestions = (
  previous: FilterSuggestions,
  entries: AgentActivityEntry[]
): FilterSuggestions => {
  let changed = false;
  const next = { ...previous };
  for (const key of TEXT_FILTER_PARAMS) {
    const values = new Set(previous[key]);
    for (const entry of entries) {
      const value = entry[key]?.trim();
      if (value) values.add(value);
    }
    if (values.size !== previous[key].length) {
      next[key] = [...values].sort((left, right) => left.localeCompare(right));
      changed = true;
    }
  }
  return changed ? next : previous;
};

type EventFilterPanelProps = {
  initialFilters: AgenticActivityFilters;
  initialTimeRange: TimeRange;
  onApply: (filters: AgenticActivityFilters, timeRange: TimeRange) => void;
  suggestions: FilterSuggestions;
};

type TextFilterFieldProps = {
  ariaLabel: string;
  filterKey: TextFilter;
  label: string;
  options: string[];
  placeholder: string;
  xlSpan: number;
};

const TextFilterField: React.FC<TextFilterFieldProps> = ({
  ariaLabel,
  filterKey,
  label,
  options,
  placeholder,
  xlSpan,
}) => (
  <Col xs={24} md={12} xl={xlSpan}>
    <Spacing asChild mb={0}>
      <Form.Item label={label} name={filterKey}>
        <AutoComplete
          allowClear
          aria-label={ariaLabel}
          className="w-full"
          options={options.map((option) => ({ value: option }))}
          placeholder={placeholder}
          suffixIcon={<DownOutlined aria-hidden="true" />}
        />
      </Form.Item>
    </Spacing>
  </Col>
);

/** Renders the server-side event filters as one apply-on-submit form. */
export const EventFilterPanel: React.FC<EventFilterPanelProps> = ({
  initialFilters,
  initialTimeRange,
  onApply,
  suggestions,
}) => {
  const [form] = Form.useForm<FilterFormValues>();
  const watchedValues = Form.useWatch([], form);

  const reset = useCallback(() => form.setFieldsValue(EMPTY_VALUES), [form]);

  const finish = useCallback(
    ({ rangeChoice, timeRange, ...rest }: FilterFormValues) =>
      onApply(rest, toTimeRange(rangeChoice, timeRange)),
    [onApply]
  );

  const isCustom = watchedValues?.rangeChoice === "custom";
  const canReset =
    (watchedValues?.rangeChoice ?? DEFAULT_LOOKBACK) !== DEFAULT_LOOKBACK ||
    TEXT_FILTER_PARAMS.some((key) => !!watchedValues?.[key]) ||
    SELECT_FILTER_PARAMS.some((key) => !!watchedValues?.[key]);

  return (
    <div className="rounded-lg border border-solid border-[var(--ant-color-border-secondary)] bg-[var(--ant-color-fill-quaternary)] p-4">
      <Form<FilterFormValues>
        aria-label="Event filters"
        form={form}
        id="agentic-event-log-filters"
        initialValues={toFormValues(initialFilters, initialTimeRange)}
        layout="vertical"
        onFinish={finish}
      >
        <Flex gap={16} vertical>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12} xl={3}>
              <Spacing asChild mb={0}>
                <Form.Item label="Protocol" name="protocol">
                  <Select<AgentActivityProtocol>
                    allowClear
                    aria-label="Filter by protocol"
                    className="w-full"
                    options={PROTOCOL_OPTIONS}
                    placeholder="Any protocol"
                  />
                </Form.Item>
              </Spacing>
            </Col>
            <Col xs={24} md={12} xl={3}>
              <Spacing asChild mb={0}>
                <Form.Item label="Risk" name="risk">
                  <Select<AgentActivityRiskLevelParam>
                    allowClear
                    aria-label="Filter by risk"
                    className="w-full"
                    options={RISK_OPTIONS}
                    placeholder="Any risk"
                  />
                </Form.Item>
              </Spacing>
            </Col>
            {FILTER_DEFINITIONS.map(
              ({ ariaLabel, key, label, placeholder, xlSpan }) => (
                <TextFilterField
                  key={key}
                  ariaLabel={ariaLabel}
                  filterKey={key}
                  label={label}
                  options={suggestions[key]}
                  placeholder={placeholder}
                  xlSpan={xlSpan}
                />
              )
            )}
          </Row>
          <Row align="bottom" gutter={[12, 12]} justify="space-between">
            <Col xs={24} md={16} xl={12}>
              <Flex align="start" gap={12} wrap>
                <Spacing asChild mb={0}>
                  <Form.Item label="Time Range" name="rangeChoice">
                    <Select<RangeChoice>
                      aria-label="Filter by time range"
                      className="min-w-44"
                      options={RANGE_CHOICE_OPTIONS}
                      // Size the list to its own content: the trigger shrinks
                      // to the selected label and would truncate the rest.
                      popupMatchSelectWidth={false}
                    />
                  </Form.Item>
                </Spacing>
                {isCustom && (
                  <Spacing asChild mb={0}>
                    {/* The label is hidden, not dropped: it keeps this item
                        the same height as the select, so showing the
                        validation error cannot move the row. */}
                    <Form.Item
                      label={<span className="invisible">Custom range</span>}
                      name="timeRange"
                      rules={CUSTOM_RANGE_RULES}
                    >
                      <CustomDatePicker.RangePicker
                        format="yyyy-MM-dd HH:mm"
                        placeholder={["Start time", "End time"]}
                        showTime={{ format: "HH:mm" }}
                      />
                    </Form.Item>
                  </Spacing>
                )}
              </Flex>
            </Col>
            <Col xs={24} md={8} xl={12}>
              <Flex gap={8} justify="end">
                <Button disabled={!canReset} htmlType="button" onClick={reset}>
                  Reset
                </Button>
                <Button htmlType="submit" type="primary">
                  Apply filters
                </Button>
              </Flex>
            </Col>
          </Row>
        </Flex>
      </Form>
    </div>
  );
};
